// Key validation (PRD 006 F1). Each "test" makes the cheapest real call that
// proves the key works browser-direct: mint an STT token, or a 1-token Claude
// ping. Nothing is stored here — the settings screen persists only on success.
//
// A fetch that REJECTS means CORS-blocked or offline (endpoint not browser-
// direct callable); a fetch that RESOLVES with any status means the request
// reached the provider — 2xx is a valid key, 401/403 a bad one.

import type { ProviderId, SpeechmaticsRegion } from './types'

export interface KeyTestResult {
  ok: boolean
  status?: number
  detail: string
}

const SPEECHMATICS_MP = 'https://mp.speechmatics.com/v1/api_keys?type=rt'
const SONIOX_TMP_KEY = 'https://api.soniox.com/v1/auth/temporary-api-key'

async function run(
  url: string,
  init: RequestInit,
  okDetail: string
): Promise<KeyTestResult> {
  let res: Response
  try {
    res = await fetch(url, init)
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    return { ok: false, detail: `Could not reach the provider (${msg}).` }
  }
  if (res.ok) return { ok: true, status: res.status, detail: okDetail }
  const body = (await res.text()).slice(0, 200)
  return { ok: false, status: res.status, detail: `HTTP ${res.status} — ${body}` }
}

function testSpeechmatics(apiKey: string): Promise<KeyTestResult> {
  return run(
    SPEECHMATICS_MP,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`
      },
      body: JSON.stringify({ ttl: 60 })
    },
    'Key valid — minted a realtime token.'
  )
}

function testSoniox(apiKey: string): Promise<KeyTestResult> {
  return run(
    SONIOX_TMP_KEY,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        usage_type: 'transcribe_websocket',
        expires_in_seconds: 60
      })
    },
    'Key valid — minted a temporary key.'
  )
}

/** Test the configured transcription provider's key. */
export function testTranscriptionKey(
  provider: ProviderId,
  apiKey: string,
  _region?: SpeechmaticsRegion
): Promise<KeyTestResult> {
  if (!apiKey.trim()) return Promise.resolve({ ok: false, detail: 'No key entered.' })
  // The token-mint endpoints are region-independent; region only selects the
  // realtime socket host, so a successful mint validates either region.
  return provider === 'soniox' ? testSoniox(apiKey) : testSpeechmatics(apiKey)
}

/** Test the bridge pairing: the gateway is reachable (GET /bridge/health) AND
 *  the token is accepted (a token-gated drain returns 200, not 401). PRD 007. */
export async function testBridge(
  bridgeUrl: string,
  token: string
): Promise<KeyTestResult> {
  const base = bridgeUrl.trim().replace(/\/+$/, '')
  if (!base) return { ok: false, detail: 'No bridge URL.' }
  if (!token.trim()) return { ok: false, detail: 'No pairing token.' }
  let health: Response
  try {
    // Health carries the token: the hosted Worker routes /bridge/* to a session
    // by the pairing token, so health can't be open (the local gateway ignores
    // the extra param). A 401 here means a bad token, not unreachable.
    health = await fetch(`${base}/bridge/health?token=${encodeURIComponent(token)}`)
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    return { ok: false, detail: `Bridge unreachable (${msg}). Is it running?` }
  }
  if (health.status === 401) return { ok: false, status: 401, detail: 'Pairing token rejected.' }
  if (!health.ok) return { ok: false, status: health.status, detail: `Bridge error HTTP ${health.status}.` }
  // Prove the token end-to-end by hitting another token-gated route.
  const drain = await fetch(`${base}/bridge/signals?since=0`, {
    headers: { 'x-bridge-token': token }
  })
  if (drain.status === 401) return { ok: false, status: 401, detail: 'Pairing token rejected.' }
  if (!drain.ok) return { ok: false, status: drain.status, detail: `HTTP ${drain.status}.` }
  return { ok: true, status: 200, detail: 'Paired — gateway reachable and token accepted.' }
}
